import { useState, useCallback, useRef, useEffect } from 'react'

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
  isFinal: boolean
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message: string
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
  // Audio/speech detection events
  onaudiostart: (() => void) | null
  onaudioend: (() => void) | null
  onsoundstart: (() => void) | null
  onsoundend: (() => void) | null
  onspeechstart: (() => void) | null
  onspeechend: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition
    webkitSpeechRecognition?: new () => SpeechRecognition
  }
}

interface UseSTTOptions {
  language?: string
  continuous?: boolean
  onTranscript?: (transcript: string, isFinal: boolean) => void
  onError?: (error: Error) => void
}

interface UseSTTReturn {
  /** Whether STT is supported in this browser */
  isSupported: boolean
  /** Whether STT is enabled */
  isEnabled: boolean
  /** Toggle STT on/off */
  setEnabled: (enabled: boolean) => void
  /** Whether currently listening */
  isListening: boolean
  /** Current interim transcript */
  transcript: string
  /** Error message if any */
  error: string | null
  /** Start listening */
  startListening: () => void
  /** Stop listening */
  stopListening: () => void
  /** Toggle listening state */
  toggleListening: () => void
}

/**
 * Hook for Speech-to-Text functionality using the Web Speech API.
 * Browser-native, no API key required.
 */
export function useSpeechToText(options: UseSTTOptions = {}): UseSTTReturn {
  // Default to continuous mode for better UX - recognition won't stop after first utterance
  const { language = 'en-US', continuous = true, onTranscript, onError } = options

  const [isSupported] = useState(() => {
    if (typeof window === 'undefined') return false
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
  })

  const [isEnabled, setIsEnabled] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const isInitializedRef = useRef(false)
  // Track if user intentionally stopped (vs recognition timing out)
  const shouldBeListeningRef = useRef(false)
  // Accumulate full transcript across multiple results
  const fullTranscriptRef = useRef('')
  // Debounce protection against accidental double-clicks
  const lastToggleTimeRef = useRef(0)

  // Use refs for callbacks to avoid re-creating recognition on every render
  const onTranscriptRef = useRef(onTranscript)
  const onErrorRef = useRef(onError)

  // Keep refs up to date
  useEffect(() => {
    onTranscriptRef.current = onTranscript
  }, [onTranscript])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // Initialize speech recognition (only once when supported)
  useEffect(() => {
    if (!isSupported || isInitializedRef.current) return

    const SpeechRecognitionClass =
      window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognitionClass) return

    const recognition = new SpeechRecognitionClass()
    recognition.continuous = continuous
    recognition.interimResults = true
    recognition.lang = language

    recognition.onstart = () => {
      console.debug('[STT] Started listening')
      setIsListening(true)
      setError(null)
      // Only clear transcript on fresh start, not on auto-restart
      if (fullTranscriptRef.current === '') {
        setTranscript('')
      }
    }

    // Audio/speech detection events (debug level only)
    recognition.onaudiostart = () => console.debug('[STT] Audio capture started')
    recognition.onaudioend = () => console.debug('[STT] Audio capture ended')
    recognition.onsoundstart = () => console.debug('[STT] Sound detected')
    recognition.onsoundend = () => console.debug('[STT] Sound ended')
    recognition.onspeechstart = () => console.debug('[STT] Speech detected')
    recognition.onspeechend = () => console.debug('[STT] Speech ended')

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Build the full transcript from all results
      let fullTranscript = ''
      let hasInterim = false

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i]
        const transcriptText = result[0].transcript

        if (result.isFinal) {
          fullTranscript += transcriptText + ' '
        } else {
          // For interim results, append to what we have so far
          fullTranscript += transcriptText
          hasInterim = true
        }
      }

      fullTranscript = fullTranscript.trim()

      // Only log final results at info level; interim at debug
      if (!hasInterim) {
        console.debug('[STT] Final transcript:', fullTranscript)
      }

      // Store final parts for accumulation across restarts
      if (!hasInterim) {
        fullTranscriptRef.current = fullTranscript
      }

      setTranscript(fullTranscript)
      onTranscriptRef.current?.(fullTranscript, !hasInterim)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('[STT] Recognition error:', event.error, event.message)

      // Handle specific error types with user-friendly messages
      let errorMessage: string | null = null

      switch (event.error) {
        case 'no-speech':
          // Not a real error - user just didn't speak, try to restart if we should be listening
          console.debug('[STT] No speech detected, will restart if still listening')
          return // Don't set isListening false - onend will handle restart
        case 'aborted':
          // User or system aborted - not an error
          setIsListening(false)
          return
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.'
          break
        case 'audio-capture':
          errorMessage = 'No microphone found. Please connect a microphone and try again.'
          break
        case 'network':
          errorMessage = 'Network error during speech recognition. Please check your connection.'
          break
        case 'service-not-allowed':
          errorMessage = 'Speech recognition service not allowed. This may be a browser restriction.'
          break
        default:
          errorMessage = `Speech recognition error: ${event.error}`
      }

      // Stop auto-restart on real errors
      shouldBeListeningRef.current = false
      setError(errorMessage)
      onErrorRef.current?.(new Error(errorMessage))
      setIsListening(false)
    }

    recognition.onend = () => {
      console.debug('[STT] onend - shouldBeListening:', shouldBeListeningRef.current)

      // If we should still be listening (user didn't manually stop), restart
      if (shouldBeListeningRef.current && recognitionRef.current) {
        console.debug('[STT] Auto-restarting recognition...')
        try {
          recognitionRef.current.start()
          return // Don't set isListening to false since we're restarting
        } catch (err) {
          console.warn('[STT] Failed to auto-restart:', err)
        }
      }

      setIsListening(false)
    }

    recognitionRef.current = recognition
    isInitializedRef.current = true
    console.debug('[STT] Initialized speech recognition')

    // Expose a test function for debugging (run window.testSpeechRecognition() in console)
    ;(window as unknown as Record<string, unknown>).testSpeechRecognition = () => {
      console.log('[STT TEST] Starting direct speech recognition test...')
      console.log('[STT TEST] Speak clearly into your microphone for 5 seconds.')
      const testRecognition = new SpeechRecognitionClass()
      testRecognition.continuous = false
      testRecognition.interimResults = true
      testRecognition.lang = 'en-US'
      testRecognition.onstart = () => console.log('[STT TEST] Started')
      testRecognition.onaudiostart = () => console.log('[STT TEST] Audio capturing')
      testRecognition.onsoundstart = () => console.log('[STT TEST] Sound detected')
      testRecognition.onspeechstart = () => console.log('[STT TEST] Speech detected')
      testRecognition.onresult = (e: SpeechRecognitionEvent) => {
        const transcript = e.results[0][0].transcript
        console.log('[STT TEST] Result:', transcript)
      }
      testRecognition.onerror = (e: SpeechRecognitionErrorEvent) => console.error('[STT TEST] Error:', e.error)
      testRecognition.onend = () => console.log('[STT TEST] Ended')
      testRecognition.start()
      setTimeout(() => testRecognition.stop(), 5000)
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort()
        } catch {
          // Ignore errors during cleanup
        }
        recognitionRef.current = null
        isInitializedRef.current = false
      }
    }
  }, [isSupported, language, continuous])

  const startListening = useCallback(() => {
    console.debug('[STT] startListening called', { isEnabled, isSupported, hasRecognition: !!recognitionRef.current })

    if (!isSupported) {
      const msg = 'Speech recognition is not supported in this browser. Please use Chrome or Edge.'
      setError(msg)
      onErrorRef.current?.(new Error(msg))
      return
    }

    if (!isEnabled) {
      console.warn('[STT] STT is not enabled. Enable it first via the voice control bar.')
      return
    }

    if (!recognitionRef.current) {
      console.error('[STT] Recognition not initialized')
      return
    }

    // Clear any previous error and transcript
    setError(null)
    fullTranscriptRef.current = ''
    setTranscript('')

    // Mark that we want to be listening (for auto-restart)
    shouldBeListeningRef.current = true

    try {
      recognitionRef.current.start()
    } catch (err) {
      // May throw if already started - that's ok
      if (err instanceof Error && err.message.includes('already started')) {
        console.debug('[STT] Already listening')
      } else {
        console.warn('[STT] Start error:', err)
        shouldBeListeningRef.current = false
      }
    }
  }, [isEnabled, isSupported])

  const stopListening = useCallback(() => {
    // Mark that we intentionally want to stop (prevent auto-restart)
    shouldBeListeningRef.current = false

    if (!recognitionRef.current) return

    try {
      recognitionRef.current.stop()
    } catch {
      // May throw if not started - that's ok
    }
  }, [])

  const toggleListening = useCallback(() => {
    // Debounce: prevent accidental double-clicks (must wait 500ms between toggles)
    const now = Date.now()
    if (now - lastToggleTimeRef.current < 500) {
      console.debug('[STT] Toggle debounced')
      return
    }
    lastToggleTimeRef.current = now

    console.debug('[STT] toggleListening called', { isListening, isEnabled })
    if (isListening) {
      stopListening()
    } else {
      startListening()
    }
  }, [isListening, startListening, stopListening])

  return {
    isSupported,
    isEnabled,
    setEnabled: setIsEnabled,
    isListening,
    transcript,
    error,
    startListening,
    stopListening,
    toggleListening,
  }
}
